import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { OnboardingFlow } from "@/components/OnboardingFlow";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";

const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Authors = lazy(() => import("./pages/Authors"));
const AuthorDetail = lazy(() => import("./pages/AuthorDetail"));
const About = lazy(() => import("./pages/About"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Firehose = lazy(() => import("./pages/Firehose"));
const HotDrama = lazy(() => import("./pages/HotDrama"));
const Resources = lazy(() => import("./pages/Resources"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const SpillTea = lazy(() => import("./pages/SpillTea"));
const Advertise = lazy(() => import("./pages/Advertise"));
const Admin = lazy(() => import("./pages/Admin"));
const ReadingHistory = lazy(() => import("./pages/ReadingHistory"));
const ForYou = lazy(() => import("./pages/ForYou"));

// Phase 2: Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Don't refetch on tab switch
      retry: 1, // Retry failed queries once
      staleTime: 60 * 1000, // Default 1 minute stale time
    },
  },
});

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <OnboardingFlow />
        <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        }>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/authors" element={<Authors />} />
          <Route path="/authors/:id" element={<AuthorDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/newsletter" element={<Newsletter />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/firehose" element={<Firehose />} />
          <Route path="/hot-drama" element={<HotDrama />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/spill-tea" element={<SpillTea />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/reading-history" element={<ReadingHistory />} />
          <Route path="/for-you" element={<ForYou />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
